/**
 * SCORE2 / SCORE2-OP Risk Data – Turkey (High Risk Region)
 * Rows = SBP (mmHg): 1: 100-119 | 2: 120-139 | 3: 140-159 | 4: 160-179
 * Cols = Non-HDL (mg/dL): 1: <155 | 2: 155-190 | 3: 191-230 | 4: >230
 * Cell value = 10-year CVD risk percentage (fatal + non-fatal)
 */

export type Gender = 'male' | 'female';
export type Smoker = true | false;

/** SBP bands: index 0 = 100-119, 1 = 120-139, 2 = 140-159, 3 = 160-179 */
export const SBP_BANDS = [
  { min: 100, max: 119 },
  { min: 120, max: 139 },
  { min: 140, max: 159 },
  { min: 160, max: 179 },
] as const;

/** Non-HDL bands: index 0 = <155, 1 = 155-190, 2 = 191-230, 3 = >230 */
export const NON_HDL_BANDS = [
  { min: 0, max: 154 },
  { min: 155, max: 190 },
  { min: 191, max: 230 },
  { min: 231, max: Infinity },
] as const;

/**
 * SCORE2: Age 40-69. Age bands in 5-year groups.
 * Structure: SCORE2_MATRICES[gender][smoker][ageBandIndex][sbpRow][nonHdlCol] = risk %
 * Age bands: 40-44, 45-49, 50-54, 55-59, 60-64, 65-69 → indices 0..5
 */
export type Score2AgeBand = 0 | 1 | 2 | 3 | 4 | 5;
export const SCORE2_AGE_BANDS = [
  { min: 40, max: 44 },
  { min: 45, max: 49 },
  { min: 50, max: 54 },
  { min: 55, max: 59 },
  { min: 60, max: 64 },
  { min: 65, max: 69 },
] as const;

/** 4x4 matrix: rows = SBP band, cols = Non-HDL band. Value = risk % */
export type RiskMatrix = number[][];

/**
 * SCORE2 risk matrices – High Risk Region (Turkey).
 * [gender][smoker][ageBand]: 4x4 matrix
 * Placeholder matrices follow the pattern from PRD; replace with official values if needed.
 */
const SCORE2_FEMALE_NONSMOKER: RiskMatrix[] = [
  // 40-44
  [
    [1, 1, 1, 1],
    [1, 1, 1, 2],
    [2, 2, 2, 2],
    [2, 3, 3, 4],
  ],
  // 45-49
  [
    [2, 2, 2, 2],
    [2, 2, 2, 3],
    [3, 3, 3, 4],
    [4, 4, 5, 5],
  ],
  // 50-54
  [
    [3, 3, 3, 4],
    [4, 4, 4, 5],
    [5, 5, 6, 6],
    [6, 7, 7, 8],
  ],
  // 55-59
  [
    [5, 5, 5, 6],
    [6, 6, 7, 7],
    [7, 8, 8, 9],
    [9, 9, 10, 11],
  ],
  // 60-64
  [
    [6, 6, 7, 7],
    [8, 8, 8, 9],
    [9, 10, 10, 11],
    [11, 12, 13, 14],
  ],
  // 65-69
  [
    [8, 8, 8, 9],
    [10, 10, 11, 11],
    [12, 13, 14, 14],
    [15, 16, 17, 18],
  ],
];

const SCORE2_FEMALE_SMOKER: RiskMatrix[] = [
  [
    [2, 2, 2, 2],
    [2, 2, 2, 3],
    [3, 3, 3, 4],
    [4, 4, 5, 6],
  ],
  [
    [3, 3, 3, 4],
    [4, 4, 4, 5],
    [5, 5, 6, 7],
    [6, 7, 8, 9],
  ],
  [
    [5, 5, 5, 6],
    [6, 6, 7, 8],
    [8, 8, 9, 10],
    [9, 10, 11, 12],
  ],
  [
    [8, 8, 8, 9],
    [9, 10, 10, 11],
    [11, 12, 13, 14],
    [14, 15, 16, 17],
  ],
  [
    [10, 10, 11, 12],
    [12, 13, 13, 14],
    [15, 15, 16, 17],
    [18, 19, 20, 21],
  ],
  [
    [13, 13, 14, 15],
    [16, 16, 17, 18],
    [19, 20, 21, 22],
    [23, 24, 25, 27],
  ],
];

const SCORE2_MALE_NONSMOKER: RiskMatrix[] = [
  [
    [2, 2, 2, 2],
    [2, 2, 2, 3],
    [3, 3, 3, 4],
    [4, 4, 5, 5],
  ],
  [
    [3, 3, 3, 4],
    [4, 4, 4, 5],
    [5, 5, 6, 7],
    [6, 7, 8, 9],
  ],
  [
    [5, 5, 5, 6],
    [6, 6, 7, 8],
    [8, 8, 9, 10],
    [10, 11, 12, 13],
  ],
  [
    [8, 8, 8, 9],
    [9, 10, 10, 11],
    [12, 12, 13, 14],
    [15, 16, 17, 18],
  ],
  [
    [11, 11, 12, 13],
    [13, 14, 14, 15],
    [16, 17, 18, 19],
    [20, 21, 22, 24],
  ],
  [
    [15, 15, 16, 17],
    [18, 18, 19, 20],
    [21, 22, 23, 24],
    [26, 27, 29, 30],
  ],
];

const SCORE2_MALE_SMOKER: RiskMatrix[] = [
  [
    [3, 3, 3, 4],
    [4, 4, 4, 5],
    [5, 5, 6, 7],
    [6, 7, 8, 9],
  ],
  [
    [5, 5, 5, 6],
    [6, 7, 7, 8],
    [8, 9, 9, 10],
    [10, 11, 12, 14],
  ],
  [
    [8, 8, 8, 9],
    [10, 10, 11, 12],
    [12, 13, 14, 15],
    [15, 16, 18, 19],
  ],
  [
    [12, 12, 13, 14],
    [15, 15, 16, 17],
    [18, 19, 20, 21],
    [22, 23, 25, 26],
  ],
  [
    [17, 17, 18, 19],
    [20, 21, 22, 23],
    [24, 25, 27, 28],
    [30, 31, 33, 35],
  ],
  [
    [22, 22, 23, 25],
    [27, 28, 29, 30],
    [32, 33, 35, 37],
    [39, 41, 43, 45],
  ],
];

/** SCORE2 lookup: [female=0|male=1][nonsmoker=0|smoker=1][ageBand] */
export const SCORE2_MATRICES: RiskMatrix[][][] = [
  [SCORE2_FEMALE_NONSMOKER, SCORE2_FEMALE_SMOKER],
  [SCORE2_MALE_NONSMOKER, SCORE2_MALE_SMOKER],
];

// --- SCORE2-OP (Age ≥70) ---
export type Score2OpAgeBand = 0 | 1 | 2 | 3 | 4;
export const SCORE2_OP_AGE_BANDS = [
  { min: 70, max: 74 },
  { min: 75, max: 79 },
  { min: 80, max: 84 },
  { min: 85, max: 89 },
  { min: 90, max: 110 },
] as const;

/** SCORE2-OP: same 4x4 SBP x Non-HDL. Age bands 70-74, 75-79, 80-84, 85-89, 90-110. */
const SCORE2_OP_FEMALE_NONSMOKER: RiskMatrix[] = [
  [
    [12, 12, 13, 14],
    [15, 15, 16, 17],
    [18, 19, 20, 21],
    [22, 23, 25, 26],
  ],
  [
    [16, 16, 17, 18],
    [20, 20, 21, 22],
    [24, 25, 26, 28],
    [29, 30, 32, 34],
  ],
  [
    [21, 21, 22, 24],
    [26, 27, 28, 29],
    [31, 32, 34, 36],
    [38, 40, 42, 44],
  ],
  [
    [27, 28, 29, 30],
    [33, 34, 35, 37],
    [40, 41, 43, 46],
    [49, 51, 53, 56],
  ],
  [
    [35, 36, 37, 39],
    [42, 43, 45, 47],
    [51, 52, 55, 58],
    [61, 64, 67, 70],
  ],
];

const SCORE2_OP_FEMALE_SMOKER: RiskMatrix[] = [
  [
    [18, 18, 19, 20],
    [22, 23, 24, 25],
    [26, 28, 29, 31],
    [33, 35, 37, 39],
  ],
  [
    [24, 24, 25, 27],
    [29, 30, 32, 33],
    [35, 37, 39, 41],
    [43, 45, 48, 50],
  ],
  [
    [31, 32, 33, 35],
    [38, 39, 41, 43],
    [46, 48, 50, 53],
    [56, 58, 61, 65],
  ],
  [
    [40, 41, 42, 44],
    [49, 50, 52, 55],
    [59, 61, 64, 67],
    [72, 75, 78, 82],
  ],
  [
    [51, 52, 54, 56],
    [62, 64, 66, 69],
    [75, 77, 80, 84],
    [91, 94, 98, 102],
  ],
];

const SCORE2_OP_MALE_NONSMOKER: RiskMatrix[] = [
  [
    [18, 18, 19, 20],
    [22, 23, 24, 25],
    [27, 28, 30, 32],
    [33, 35, 37, 39],
  ],
  [
    [24, 24, 26, 27],
    [30, 31, 33, 35],
    [36, 38, 40, 42],
    [44, 46, 49, 52],
  ],
  [
    [32, 33, 34, 36],
    [40, 41, 43, 46],
    [48, 50, 53, 56],
    [59, 61, 65, 68],
  ],
  [
    [42, 43, 45, 47],
    [52, 54, 56, 59],
    [63, 65, 69, 72],
    [77, 80, 84, 88],
  ],
  [
    [54, 55, 58, 60],
    [67, 69, 72, 76],
    [81, 84, 88, 92],
    [99, 103, 108, 113],
  ],
];

const SCORE2_OP_MALE_SMOKER: RiskMatrix[] = [
  [
    [27, 28, 29, 31],
    [34, 35, 37, 39],
    [41, 43, 45, 48],
    [50, 52, 55, 58],
  ],
  [
    [36, 37, 39, 41],
    [45, 47, 49, 52],
    [55, 57, 60, 63],
    [67, 70, 74, 77],
  ],
  [
    [48, 49, 51, 54],
    [60, 62, 65, 68],
    [73, 76, 79, 83],
    [89, 93, 97, 102],
  ],
  [
    [63, 64, 67, 70],
    [78, 81, 84, 88],
    [95, 99, 103, 108],
    [116, 121, 126, 132],
  ],
  [
    [81, 83, 86, 90],
    [101, 104, 109, 114],
    [123, 128, 133, 139],
    [150, 156, 163, 170],
  ],
];

/** SCORE2-OP: [female=0|male=1][nonsmoker=0|smoker=1][ageBand] */
export const SCORE2_OP_MATRICES: RiskMatrix[][][] = [
  [SCORE2_OP_FEMALE_NONSMOKER, SCORE2_OP_FEMALE_SMOKER],
  [SCORE2_OP_MALE_NONSMOKER, SCORE2_OP_MALE_SMOKER],
];

/** Diabetes multiplier for SCORE2-OP */
export const SCORE2_OP_DIABETES_MULTIPLIER = 1.3;
